import { apiErrorEnvelopeSchema } from "@lafayette/schemas";
import { createIdempotencyKey } from "./idempotency-key";
/** The envelope code of a status, for an error answer that has no envelope (a proxy page). */
const STATUS_CODES = {
    400: "invalid_request",
    401: "unauthenticated",
    403: "forbidden",
    404: "not_found",
    408: "request_timeout",
    409: "conflict",
    413: "payload_too_large",
    415: "invalid_request",
    422: "unprocessable",
    429: "rate_limited",
    503: "service_unavailable",
};
const wait = (milliseconds) => milliseconds <= 0
    ? Promise.resolve()
    : new Promise((resolve) => setTimeout(resolve, milliseconds));
/** `Retry-After` as whole seconds (0 to a day), or `null` for a missing, negative or non-numeric value (a date form is not used by this API). */
function retryAfterOf(header) {
    if (header === null || !/^\d{1,6}$/.test(header.trim()))
        return null;
    const seconds = Number(header.trim());
    return seconds <= 86_400 ? seconds : null;
}
/** The JSON of a response, or `undefined` when the body is empty or is not JSON. */
export async function readJson(response) {
    const text = await response.text();
    try {
        return text === "" ? undefined : JSON.parse(text);
    }
    catch {
        return undefined;
    }
}
/** The HTTP error of a non-2xx answer: the envelope when the body has one, else a status-only error. */
export function httpErrorOf(response, body, errors, refusedMessage = errors.refusedMessage) {
    const retryAfter = retryAfterOf(response.headers.get("retry-after"));
    const envelope = apiErrorEnvelopeSchema.safeParse(body);
    if (envelope.success) {
        return errors.http({
            status: response.status,
            code: envelope.data.error.code,
            message: envelope.data.error.message,
            requestId: envelope.data.requestId,
            retryAfter,
            body,
        });
    }
    return errors.http({
        status: response.status,
        code: STATUS_CODES[response.status] ?? "unknown",
        message: refusedMessage,
        requestId: response.headers.get("x-request-id"),
        retryAfter,
        body,
    });
}
export function createJsonTransport(options, errors) {
    const base = options.baseUrl.endsWith("/") ? options.baseUrl : `${options.baseUrl}/`;
    const maxRetries = options.maxNetworkRetries ?? 2;
    const retryDelayMs = options.retryDelayMs ?? 250;
    return async function send(call) {
        const url = new URL(call.path.replace(/^\//, ""), base);
        if (call.query !== undefined && [...call.query.keys()].length > 0) {
            url.search = call.query.toString();
        }
        const headers = { ...call.headers, accept: "application/json" };
        const token = await options.getAccessToken?.();
        if (token)
            headers.authorization = `Bearer ${token}`;
        if (call.method !== "GET" && call.command !== false) {
            headers["content-type"] = "application/json";
            // One key for the call: every network retry below sends the same one.
            headers["idempotency-key"] = call.idempotencyKey ?? createIdempotencyKey();
        }
        const fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
        const init = {
            method: call.method,
            headers,
            ...(call.body === undefined ? {} : { body: JSON.stringify(call.body) }),
            ...(call.signal === undefined ? {} : { signal: call.signal }),
        };
        let response;
        let failure;
        const retries = call.maxNetworkRetries ?? maxRetries;
        for (let attempt = 0; attempt <= retries; attempt += 1) {
            if (attempt > 0)
                await wait(attempt * retryDelayMs);
            try {
                response = await fetcher(url, init);
                break;
            }
            catch (error) {
                if (call.signal?.aborted === true)
                    throw error;
                failure = error;
            }
        }
        if (response === undefined)
            throw errors.network(failure);
        const json = await readJson(response);
        if (!response.ok)
            throw httpErrorOf(response, json, errors, call.refusedMessage);
        const data = typeof json === "object" && json !== null && "data" in json ? json.data : undefined;
        const parsed = call.schema.safeParse(data);
        if (!parsed.success)
            throw errors.response();
        return parsed.data;
    };
}
